import React, { memo, useCallback, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { useHistory } from "react-router-dom";
import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
} from "@progress/kendo-react-charts";
import { Skeleton } from "@progress/kendo-react-indicators";

import ChartNavigation from "src/components/ChartNavigation";
import useChartNavigation from "src/hooks/useChartNavigation";
import { getStatusByValue } from "src/components/StatusBadge/status";

import {
  ChartCard,
  ChartToggle,
  CHART_COLORS,
  centerWindow,
  createGradientBarVisual,
  EARNED_GRADIENT,
  PROJECTED_GRADIENT,
  resolveVisibleCount,
} from "../charts";
import { useEarningsOverview } from "../../hooks";
import {
  positionOptions as buildPositionOptions,
  positionedRows,
  scopeParams,
  showProfileControl,
} from "../../../providerPayRequestDashboard/utils/selectedProfile";
import "./EarningsOverviewChart.scss";

/*
 * Earnings Overview (graph1). Web counterpart of the mobile
 * EarningsOverviewContent screen, drawn with KendoReact instead of the mobile
 * app's hand-rolled SVG CustomBarChart.
 *
 * Mobile keeps a fixed 80px pitch per period and scrolls the overflow, so the
 * bars stay the same width however much history a provider has. Kendo would
 * otherwise squeeze every period into the card, so the categories are windowed
 * instead: the window is sized from the measured width and re-centred on the
 * selected period, which is the same effect by different means.
 *
 * Clicking a bar selects that period and refreshes the pay group rows below.
 */

const CHART_STYLE = { height: "290px", width: "100%" };
const CHART_AREA = { margin: { top: 12, right: 8, bottom: 4, left: 4 } };

const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

/* Mobile's y-axis formatter: 25000 -> "25.0k". */
const compactValue = (value) => (Math.abs(value) >= 1000 ? `${(value / 1000).toFixed(1)}k` : `${value}`);

const AXIS_LABELS = {
  font: "12px Poppins, sans-serif",
  color: CHART_COLORS.mutedLabel,
  content: (e) => compactValue(e.value),
};

const CATEGORY_LABELS = {
  font: "12px Poppins, sans-serif",
  color: CHART_COLORS.mutedLabel,
  margin: { top: 8 },
};

const HIDDEN_GRID = { visible: false };
const AXIS_LINE = { color: CHART_COLORS.axisLine };

const PayGroupRow = ({ name, value, isGrandTotal = false, onClick }) => (
  <div
    className={`earnings-overview__row${isGrandTotal ? " earnings-overview__row--total" : ""}${onClick ? " earnings-overview__row--clickable" : ""}`}
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => (e.key === "Enter" || e.key === " ") && onClick(e) : undefined}
  >
    <span className="earnings-overview__row-name">{name}</span>
    <span className="earnings-overview__row-value">{currency.format(value || 0)}</span>
  </div>
);

const PayGroupSkeleton = () => (
  <div className="earnings-overview__row earnings-overview__row--skeleton">
    <Skeleton shape="text" style={{ width: "30%" }} />
    <Skeleton shape="text" style={{ width: "18%" }} />
  </div>
);

const EarningsOverviewChart = ({ providerId, profileRows = [], periodTypes }) => {
  /*
   * The card's own Position pick, one position at a time and independent of the
   * page's Position filter. It starts on the primary profile (row 0), or the first
   * position when row 0 has none, and is not stored. Hidden for one position.
   */
  const showPositionPicker = showProfileControl(profileRows);
  const positionOptions = useMemo(
    () => (showPositionPicker ? buildPositionOptions(profileRows) : []),
    [showPositionPicker, profileRows]
  );
  const [pickedPositionKey, setPickedPositionKey] = useState(null);
  const pickedRow = useMemo(() => {
    if (!showPositionPicker) return null;
    const positioned = positionedRows(profileRows);
    return (
      positioned.find((row) => row.org_position_key === pickedPositionKey) ??
      positioned.find((row) => row.org_position_key === profileRows[0]?.org_position_key) ??
      positioned[0] ??
      null
    );
  }, [showPositionPicker, profileRows, pickedPositionKey]);
  const selectedPosition = positionOptions.find((option) => option.id === pickedRow?.org_position_key) ?? null;
  const earningsProviderId = pickedRow?.provider_mstr_key ?? providerId;

  const handlePositionChange = useCallback((option) => {
    if (option) setPickedPositionKey(option.id);
  }, []);

  const {
    periodOptions,
    selectedPeriodType,
    selectPeriodType,
    chartData,
    visibleSeries,
    highlightedCategory,
    highlightedIndex,
    selectCategory,
    selectedPeriodStartDate,
    selectedPeriodEndDate,
    payGroups,
    grandTotal,
    isChartLoading,
    isPayGroupLoading,
  } = useEarningsOverview(earningsProviderId, periodTypes);

  const history = useHistory();

  const [selectedYear, setSelectedYear] = useState(null);

  const yearOptions = useMemo(() => {
    const options = chartData.map((item) => ({
      text: item.category,
      value: item.periodKey,
    }));
    return options.reverse();
  }, [chartData]);

  useEffect(() => {
    if (!yearOptions.length) return;

    /*
     * Keep the dropdown in sync with whichever period is actually highlighted
     * - including a bar click - rather than only the initial default.
     */
    const match = highlightedCategory ? yearOptions.find((option) => option.text === highlightedCategory) : null;
    setSelectedYear(match || yearOptions[0]);
  }, [yearOptions, highlightedCategory]);

  const categories = useMemo(() => chartData.map((point) => point.category), [chartData]);

  /*
   * How many periods fit is driven by the rendered width, mirroring mobile's
   * screenWidth math rather than a hardcoded count.
   */
  const plotRef = useRef(null);
  const [plotWidth, setPlotWidth] = useState(0);

  useEffect(() => {
    const element = plotRef.current;
    if (!element) return undefined;

    const updateWidth = () => setPlotWidth(element.clientWidth);
    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  const visibleCount = useMemo(() => resolveVisibleCount(plotWidth), [plotWidth]);

  const nav = useChartNavigation(0, visibleCount, categories);
  const { setRange } = nav;

  /*
   * Re-centre on the selected period, the way mobile scrolls the selected group
   * to the middle. This also lands the initial window on the most recent
   * periods, since the first calendar row is the latest one.
   */
  useEffect(() => {
    if (highlightedIndex < 0 || !categories.length) return;
    setRange(centerWindow(highlightedIndex, visibleCount, categories.length));
  }, [highlightedIndex, visibleCount, categories.length, setRange]);

  /*
   * Only the selected period's bar is opaque. The other bars are faded and
   * carry a solid accent cap, per the design.
   */
  const earnedVisual = useMemo(
    () =>
      createGradientBarVisual({
        stops: EARNED_GRADIENT,
        isSelected: (e) => e.category === highlightedCategory,
      }),
    [highlightedCategory]
  );

  const projectedVisual = useMemo(
    () =>
      createGradientBarVisual({
        stops: PROJECTED_GRADIENT,
        isSelected: (e) => e.category === highlightedCategory,
        cap: false,
      }),
    [highlightedCategory]
  );

  /*
   * onSeriesClick only fires on a rendered bar, so a period with no data (a
   * zero-height bar) is never clickable. onPlotAreaClick fires from the
   * category's column area regardless of bar height, so it covers both cases.
   */
  const handlePlotAreaClick = useCallback(
    (e) => {
      if (e.category) selectCategory(e.category);
    },
    [selectCategory]
  );

  /*
   * Same destination as AllDates' "View All" (/pay_request_detail), but
   * scoped down to the clicked pay group's task category and the currently
   * selected period's date range.
   */
  const handleCardNavigation = useCallback(
    (group) => {
      if (!group.earnedValue) return;

      // The picked position travels to the detail page; with the picker hidden there are no lists.
      const lists = pickedRow ? scopeParams(profileRows, [pickedRow.org_position_key]) : {};

      history.push({
        pathname: "/pay_request_detail",
        state: {
          provider_id: earningsProviderId,
          provider_ids: lists.in_provider_ids ?? null,
          org_position_keys: lists.in_org_position_keys ?? null,
          startDate: selectedPeriodStartDate,
          endDate: selectedPeriodEndDate,
          contract_type_mstr_key: group.taskCategoryMstrKey,
          timesheet_status_mstr_key: getStatusByValue("sent_to_payroll")?.timesheet_status_mstr_key,
        },
      });
    },
    [history, earningsProviderId, pickedRow, profileRows, selectedPeriodStartDate, selectedPeriodEndDate]
  );

  const handleYearChange = useCallback(
    (e) => {
      setSelectedYear(e);
      selectCategory(e.text);
    },
    [selectCategory]
  );

  const renderTooltip = useCallback(
    ({ point }) => (
      <div className="earnings-overview__tooltip">
        <span className="earnings-overview__tooltip-label">{point?.category}</span>
        <span className="earnings-overview__tooltip-value">
          {point?.series?.name} : {currency.format(point?.value || 0)}
        </span>
      </div>
    ),
    []
  );

  const toggles = periodOptions.length ? (
    <ChartToggle
      options={periodOptions}
      value={selectedPeriodType}
      onChange={selectPeriodType}
      ariaLabel="Earnings period"
    />
  ) : null;

  const currentPeriodLabel = periodOptions.find((opt) => opt.value === selectedPeriodType)?.label || "Period";

  return (
    <ChartCard
      title="Earnings Overview"
      toggles={toggles}
      yearOptions={yearOptions}
      selectedYear={selectedYear}
      onYearChange={handleYearChange}
      positionOptions={positionOptions}
      selectedPosition={selectedPosition}
      onPositionChange={handlePositionChange}
      periodTypeLabel={currentPeriodLabel}
      isLoading={isChartLoading}
      isEmpty={!chartData.length}
    >
      {/* Positioned so ChartNavigation can place itself over the plot area. */}
      <div className="earnings-overview__plot" ref={plotRef}>
        <ChartNavigation
          min={nav.range.min}
          max={nav.range.max}
          totalItems={categories.length}
          onNavigateLeft={nav.navigateLeft}
          onNavigateRight={nav.navigateRight}
          onReset={nav.resetRange}
        />

        <Chart
          key={`earnings-${nav.range.min}-${nav.range.max}`}
          style={CHART_STYLE}
          chartArea={CHART_AREA}
          renderAs="svg"
          transitions={false}
          pannable={false}
          zoomable={false}
          onPlotAreaClick={handlePlotAreaClick}
        >
          <ChartLegend visible={visibleSeries.projected && visibleSeries.earned} position="bottom" />
          <ChartTooltip render={renderTooltip} />

          <ChartCategoryAxis>
            <ChartCategoryAxisItem
              categories={categories}
              min={nav.range.min}
              max={nav.range.max}
              labels={CATEGORY_LABELS}
              majorGridLines={HIDDEN_GRID}
              line={AXIS_LINE}
            />
          </ChartCategoryAxis>

          <ChartValueAxis>
            <ChartValueAxisItem labels={AXIS_LABELS} majorGridLines={HIDDEN_GRID} line={AXIS_LINE} />
          </ChartValueAxis>

          {/*
           * Kendo's ChartSeries propTypes walk children and read child.type
           * without a null guard, so a `cond ? item : null` child throws inside
           * the validator. Pass one flat array with the hidden series removed.
           */}
          <ChartSeries>
            {[
              visibleSeries.projected && (
                <ChartSeriesItem
                  key="projected"
                  type="column"
                  name="Projected"
                  data={chartData}
                  field="projected"
                  categoryField="category"
                  color={CHART_COLORS.projected}
                  visual={projectedVisual}
                />
              ),
              visibleSeries.earned && (
                <ChartSeriesItem
                  key="earned"
                  type="column"
                  name="Earned"
                  data={chartData}
                  field="earned"
                  categoryField="category"
                  color={CHART_COLORS.earnedFrom}
                  visual={earnedVisual}
                />
              ),
            ].filter(Boolean)}
          </ChartSeries>
        </Chart>
      </div>

      {isPayGroupLoading ? (
        <div className="earnings-overview__rows">
          <PayGroupSkeleton />
          <PayGroupSkeleton />
        </div>
      ) : (
        <>
          {/* Capped and scrollable; see the height note in the stylesheet. */}
          <div className="earnings-overview__rows">
            {payGroups.map((group) => {
              return (
                <PayGroupRow
                  key={group.key}
                  name={group.name}
                  value={group.earnedValue}
                  onClick={() => handleCardNavigation(group)}
                />
              );
            })}
          </div>

          {/* Pinned outside the scroll area - the total must never scroll away. */}
          {grandTotal ? <PayGroupRow name="Grand Total" value={grandTotal.earnedValue} isGrandTotal /> : null}
        </>
      )}
    </ChartCard>
  );
};

export default memo(EarningsOverviewChart);
